<main class="dashboard-page">
  <section class="dashboard-hero">
    <div>
      <h1>
        @if (dashboardRole() === 'account_self_service') { Account Settings }
        @else if (dashboardRole() === 'user') { API Keys }
        @else if (dashboardRole() === 'admin') { Admin }
      </h1>
    </div>
    <button type="button" class="ghost-action" (click)="logout()">
      {{ loadingAction === 'logout' ? 'Logging out...' : 'Log Out' }}
    </button>
  </section>

  @if (toastVisible) {
  <div
    class="toast"
    [class.success]="toastType === 'success'"
    [class.error]="toastType === 'error'"
    role="status"
    aria-live="polite"
  >
    <div class="toast-content">
      <span class="toast-icon" aria-hidden="true">
        @if (toastType === 'success') { ✓ } @else { ⚠ }
      </span>
      <span class="toast-message">{{ toastMessage }}</span>
      <button type="button" class="toast-close" (click)="hideToast()" aria-label="Dismiss notification">
        ×
      </button>
    </div>
  </div>
  }

  @if (!roles().includes(dashboardRole())) {
  <article class="dashboard-card dashboard-card--wide">
    <h2>Dashboard unavailable</h2>
    <p class="field-note">
      This account does not currently have access to this dashboard.
    </p>
  </article>
  } @else if (dashboardRole() === 'account_self_service') {
  <section class="dashboard-grid" aria-label="Account self-service tools">
    <article class="dashboard-card">
      <div class="card-header">
        <h2>Account</h2>
        @if (!isSelfServiceEditing) {
        <button type="button" class="ghost-action" (click)="startSelfServiceEdit()">
          Edit
        </button>
        }
      </div>

      @if (!isSelfServiceEditing) {
      <dl class="account-summary">
        <div>
          <dt>Name</dt>
          <dd>{{ selfServiceForm.controls.name.value || 'Not set' }}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>{{ selfServiceForm.controls.email.value || 'Not set' }}</dd>
        </div>
        <div>
          <dt>Monthly plan</dt>
          <dd>
            @for (plan of plans; track plan.id) {
            @if (plan.id === selfServiceForm.controls.monthlyPlanId.value) {
            {{ plan.name }} - {{ plan.crypts }}
            }
            }
          </dd>
        </div>
      </dl>
      } @else {
      <form
        [formGroup]="selfServiceForm"
        (ngSubmit)="saveSelfService()"
        class="dashboard-form dashboard-form--stacked"
      >
        <label>
          <span>Name</span>
          <input type="text" formControlName="name" autocomplete="name" />
          @if (selfServiceForm.controls.name.invalid &&
          selfServiceForm.controls.name.touched) {
          <small>Name is required.</small>
          }
        </label>

        <label>
          <span>Email</span>
          <input type="email" formControlName="email" autocomplete="email" />
          @if (selfServiceForm.controls.email.invalid &&
          selfServiceForm.controls.email.touched) {
          <small>Please enter a valid email.</small>
          }
        </label>

        <label>
          <span>Plan</span>
          <select formControlName="monthlyPlanId">
            @for (plan of plans; track plan.id) {
            <option [ngValue]="plan.id">
              {{ plan.name }} - {{ plan.crypts }}
            </option>
            }
          </select>
        </label>

        <div class="password-edit-group">
          <h3>Password</h3>
          <label>
            <span>Current password</span>
            <input
              type="password"
              formControlName="oldPassword"
              autocomplete="current-password"
              placeholder="Current password"
            />
            @if (selfServiceForm.controls.oldPassword.invalid &&
            selfServiceForm.controls.oldPassword.touched) {
            <small>Current password is required to change password.</small>
            }
          </label>
          <label>
            <span>New password</span>
            <input
              type="password"
              formControlName="password"
              autocomplete="new-password"
              placeholder="At least 8 characters"
            />
            @if (selfServiceForm.controls.password.invalid &&
            selfServiceForm.controls.password.touched) {
            <small>Password must be at least 8 characters.</small>
            }
          </label>
          <label>
            <span>Re-enter new password</span>
            <input
              type="password"
              formControlName="confirmPassword"
              autocomplete="new-password"
              placeholder="Re-enter new password"
            />
            @if (selfServiceForm.controls.confirmPassword.hasError('mismatch')) {
            <small>New passwords do not match.</small>
            } @else if (selfServiceForm.controls.confirmPassword.invalid &&
            selfServiceForm.controls.confirmPassword.touched) {
            <small>Please re-enter the new password.</small>
            }
          </label>
        </div>

        <div class="dashboard-actions">
          <button type="submit" class="primary-action" [disabled]="!!loadingAction">
            {{ loadingAction === 'self-service-save' ? 'Saving...' : 'Save Changes' }}
          </button>
          <button
            type="button"
            class="ghost-action"
            (click)="cancelSelfServiceEdit()"
            [disabled]="!!loadingAction"
          >
            Cancel
          </button>
        </div>
      </form>
      }
    </article>
  </section>
  } @else {
  <div class="folder-stack" [class.folder-stack--solo]="dashboardPanels().length <= 1">
  @if (dashboardPanels().length > 1) {
  <nav class="dashboard-tool-menu" aria-label="Dashboard tools">
    @for (panel of dashboardPanels(); track panel.id) {
    <button
      type="button"
      class="tool-menu-button"
      [class.active]="activePanel() === panel.id"
      (click)="selectPanel(panel.id)"
    >
      <span class="tool-menu-button-label">{{ panel.label }}</span>
    </button>
    }
  </nav>
  }
  <section class="dashboard-grid" [attr.aria-label]="dashboardRole()">
    @if (dashboardRole() === 'user') {
    @if (activePanel() === 'api-key') {
    <article class="dashboard-card dashboard-card--wide">
      <h2>API key</h2>
      <section class="api-key-summary" aria-label="API key summary">
        <div class="card-header">
          <div>
            <h3>Current usage</h3>
            <p class="field-note">
              Crypts are counted when your API key encrypts or decrypts data.
            </p>
          </div>
          <button
            type="button"
            class="ghost-action"
            (click)="loadApiKeySummary(true)"
            [disabled]="apiKeySummaryLoading()"
          >
            {{ apiKeySummaryLoading() ? 'Refreshing...' : 'Refresh' }}
          </button>
        </div>

        @if (apiKeySummary(); as summary) {
        <dl class="api-key-stats">
          <div>
            <dt>Current key expires</dt>
            <dd>
              @if (summary.apiKey) {
              {{ formatDateTime(summary.apiKey.expiresAt) }}
              } @else {
              No active API key
              }
            </dd>
          </div>
          <div>
            <dt>Plan allows</dt>
            <dd>{{ formatNumber(summary.plan.cryptsPerMonth) }} crypts / month</dd>
          </div>
          <div>
            <dt>Used this month</dt>
            <dd>
              {{ formatNumber(summary.usage.cryptsUsedThisMonth) }} crypts
              @if (summary.plan.cryptsPerMonth != null) {
              of {{ formatNumber(summary.plan.cryptsPerMonth) }}
              }
            </dd>
          </div>
          <div>
            <dt>Month rolls over</dt>
            <dd>{{ formatDateTime(summary.usage.monthRollsOverAt) }}</dd>
          </div>
        </dl>
        @if (apiKeyUsagePercent() !== null) {
        <div class="crypt-progress" aria-label="Monthly crypt usage">
          <span [style.width.%]="apiKeyUsagePercent()"></span>
        </div>
        }
        } @else {
        <p class="field-note">
          {{ apiKeySummaryLoading() ? 'Loading API key details...' : 'API key details are not available yet.' }}
        </p>
        }
      </section>

      <form
        [formGroup]="apiKeyForm"
        (ngSubmit)="createApiKey()"
        class="dashboard-form api-key-form"
      >
        <label>
          <span>Description</span>
          <input
            type="text"
            formControlName="description"
            placeholder="Production app, staging, Zapier, etc."
          />
        </label>

        <div
          class="themed-select"
          (appClickOutside)="closeExpiresDropdown()"
        >
          <span class="field-label">Expires</span>
          <button
            type="button"
            class="themed-select-trigger"
            (click)="toggleExpiresOpen()"
            [attr.aria-expanded]="expiresOpen"
          >
            {{ expiresInLabel() }}
            <span
              class="role-caret"
              [class.role-caret--open]="expiresOpen"
              aria-hidden="true"
            ></span>
          </button>
          @if (expiresOpen) {
          <div class="themed-select-panel" role="listbox">
            @for (option of expiresOptions; track option.value) {
            <button
              type="button"
              class="themed-select-option"
              role="option"
              [class.themed-select-option--selected]="
                apiKeyForm.controls.expiresIn.value === option.value
              "
              [attr.aria-selected]="
                apiKeyForm.controls.expiresIn.value === option.value
              "
              (click)="selectExpiresIn(option.value)"
            >
              {{ option.label }}
            </button>
            }
          </div>
          }
        </div>

        <button type="submit" class="primary-action" [disabled]="!!loadingAction">
          {{ loadingAction === 'api-key' ? 'Creating...' : 'Create New API Key' }}
        </button>
      </form>

      @if (createdApiKey) {
      <div class="api-key-result">
        <div>
          <span class="result-label">New API key</span>
          <div class="api-key-value">
            <code>{{ displayedApiKey() }}</code>
            <button
              type="button"
              class="api-key-visibility"
              (click)="toggleApiKeyVisibility()"
              [attr.aria-label]="apiKeyRevealed ? 'Hide API key' : 'Show API key'"
              [attr.aria-pressed]="apiKeyRevealed"
            >
              @if (apiKeyRevealed) {
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                  d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 12.5a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 .001 6.001A3 3 0 0 0 12 9z"
                  fill="currentColor"
                />
              </svg>
              } @else {
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                  d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75C21.27 7.61 17 4.5 12 4.5c-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78 3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
                  fill="currentColor"
                />
              </svg>
              }
            </button>
          </div>
          <p>{{ createdApiKey.storeKeyMessage }}</p>
        </div>
        <button type="button" class="ghost-action" (click)="copyApiKey()">
          Copy
        </button>
      </div>
      }
    </article>
    }

    } @else if (dashboardRole() === 'admin') {
    @if (activePanel() === 'service-operations') {
    <article class="dashboard-card dashboard-card--wide">
      <h2>Service operations</h2>
      <form [formGroup]="rotationForm" class="dashboard-form dashboard-form--stacked">
        <label>
          <span>Operations password</span>
          <input type="password" formControlName="operationsPassword" />
        </label>
        <label>
          <span>Rotation password</span>
          <input type="password" formControlName="rotationPassword" />
        </label>
        <label class="checkbox-label">
          <input type="checkbox" formControlName="force" />
          <span>Force key encryption key rotation</span>
        </label>
        <div class="dashboard-actions">
          <button type="button" class="primary-action" (click)="rotateKeyEncryptionKey()" [disabled]="!!loadingAction">
            Rotate KEK
          </button>
          <button type="button" class="ghost-action" (click)="forceFreshStart()" [disabled]="!!loadingAction">
            Force Fresh Start
          </button>
        </div>
      </form>

      @if (serviceStatusLoading() && !serviceStatus()) {
      <p class="field-note">Loading service status...</p>
      } @else if (serviceStatus(); as status) {
      <section class="panel-status-result" aria-label="Service status">
        <h3>Service status</h3>
        @for (table of resultTables(status); track table.title) {
        <div class="result-table-block">
          <h3>{{ table.title }}</h3>
          <div class="result-table-wrap">
            <table class="result-table">
              <thead>
                <tr>
                  @for (column of table.columns; track column) {
                  <th>{{ formatColumn(column) }}</th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (row of table.rows; track $index) {
                <tr>
                  @for (column of table.columns; track column) {
                  <td>{{ formatCell(row[column]) }}</td>
                  }
                </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
        }
      </section>
      }
    </article>
    }

    @if (activePanel() === 'destructive-password') {
    <article class="dashboard-card dashboard-card--wide">
      <h2>Destructive operations password</h2>

      @if (destructivePasswordStatusLoading() && !destructivePasswordStatus()) {
      <p class="field-note">Loading password status...</p>
      } @else if (destructivePasswordStatus(); as status) {
      <section class="panel-status-result" aria-label="Destructive password status">
        <h3>Password status</h3>
        @for (table of resultTables(status); track table.title) {
        <div class="result-table-block">
          <h3>{{ table.title }}</h3>
          <div class="result-table-wrap">
            <table class="result-table">
              <thead>
                <tr>
                  @for (column of table.columns; track column) {
                  <th>{{ formatColumn(column) }}</th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (row of table.rows; track $index) {
                <tr>
                  @for (column of table.columns; track column) {
                  <td>{{ formatCell(row[column]) }}</td>
                  }
                </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
        }
      </section>
      }

      <form
        [formGroup]="destructivePasswordRotateForm"
        class="dashboard-form dashboard-form--stacked destructive-rotate-form"
      >
        <label>
          <span>Old password</span>
          <input type="password" formControlName="oldOperationsPassword" />
        </label>
        <label>
          <span>New password</span>
          <input type="password" formControlName="newOperationsPassword" />
        </label>
        <label>
          <span>New password again</span>
          <input type="password" formControlName="confirmNewOperationsPassword" />
        </label>
        <button
          type="button"
          class="primary-action"
          (click)="rotateDestructiveOpsPassword()"
          [disabled]="!!loadingAction"
        >
          {{ loadingAction === 'destructive-password-rotate' ? 'Rotating...' : 'Rotate' }}
        </button>
      </form>
    </article>
    }

    @if (activePanel() === 'validation') {
    <article class="dashboard-card dashboard-card--wide">
      <h2>Validation signups</h2>
      <p class="field-note">
        Emails collected by the validation service, including survey answers and
        beta applications.
      </p>

      <div class="result-table-wrap whitelist-table-wrap">
        <table class="result-table whitelist-table validation-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Signed up</th>
              <th>Survey</th>
              <th>Beta tester</th>
              <th>Whitelist</th>
              <th class="whitelist-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            @if (validationLoading() && validationEntries().length === 0) {
            <tr>
              <td colspan="6">Loading validation emails...</td>
            </tr>
            } @else if (validationEntries().length === 0) {
            <tr>
              <td colspan="6">No validation emails found.</td>
            </tr>
            } @else {
            @for (entry of validationEntries(); track entry.id) {
            <tr>
              <td>{{ entry.email }}</td>
              <td>{{ formatWhitelistDate(entry.created) }}</td>
              <td>
                <div class="validation-survey-cell">
                  <span>{{ formatSurveySummary(entry) }}</span>
                  @if (hasSurveyDetails(entry)) {
                  <details class="validation-survey-details">
                    <summary>Details</summary>
                    <ul>
                      <li>Q1 vibe coding: {{ formatBool(entry.survey.q1) }}</li>
                      <li>Q2 sessions/mo: {{ formatSurveyNumber(entry.survey.q2) }}</li>
                      <li>Q3 sensitive data: {{ formatBool(entry.survey.q3) }}</li>
                      <li>Q4 safe storage plan: {{ formatBool(entry.survey.q4) }}</li>
                      <li>Q5 spend: {{ formatSurveyNumber(entry.survey.q5) }}</li>
                      <li>Q6 comments: {{ entry.survey.q6 || '—' }}</li>
                    </ul>
                  </details>
                  }
                </div>
              </td>
              <td>{{ formatBetaTester(entry) }}</td>
              <td>
                @if (entry.whitelisted) {
                <span class="status-pill status-pill--ok">Whitelisted</span>
                } @else {
                <span class="status-pill status-pill--muted">Not whitelisted</span>
                }
              </td>
              <td class="whitelist-actions-col">
                <button
                  type="button"
                  class="ghost-action"
                  (click)="enrollValidationEmail(entry)"
                  [disabled]="
                    !!loadingAction ||
                    entry.whitelisted ||
                    !canEnrollValidationEmail()
                  "
                >
                  @if (enrollingValidationEmail === entry.email) {
                  Enrolling...
                  } @else {
                  Enroll in whitelist
                  }
                </button>
              </td>
            </tr>
            }
            }
          </tbody>
        </table>
      </div>

      <div class="whitelist-pagination">
        <button
          type="button"
          class="ghost-action"
          (click)="goValidationPage(-1)"
          [disabled]="!canGoValidationPrev() || !!loadingAction"
        >
          Previous
        </button>
        <span>
          Page {{ validationPage() }} of {{ validationTotalPages() }}
          ({{ validationTotal() }} total)
        </span>
        <button
          type="button"
          class="ghost-action"
          (click)="goValidationPage(1)"
          [disabled]="!canGoValidationNext() || !!loadingAction"
        >
          Next
        </button>
      </div>
    </article>
    }

    @if (activePanel() === 'whitelist') {
    <article class="dashboard-card dashboard-card--wide">
      <h2>Whitelist</h2>

      <form [formGroup]="whitelistCreateForm" class="dashboard-form whitelist-add-form">
        <label>
          <span>Email</span>
          <input type="email" formControlName="email" />
        </label>
        <div class="role-multiselect" (appClickOutside)="closeRoleDropdowns()">
          <span class="field-label">Roles</span>
          <button
            type="button"
            class="role-multiselect-trigger"
            (click)="toggleCreateRolesOpen()"
            [attr.aria-expanded]="createRolesOpen"
          >
            {{ selectedRolesLabel(createRoleUuids) }}
            <span class="role-caret" [class.role-caret--open]="createRolesOpen" aria-hidden="true"></span>
          </button>
          @if (createRolesOpen) {
          <div class="role-multiselect-panel">
            @for (role of whitelistRoles(); track role.uuid) {
            <label class="role-option">
              <input
                type="checkbox"
                [checked]="isCreateRoleSelected(role.uuid)"
                (change)="toggleCreateRole(role.uuid)"
              />
              <span>{{ role.name }}</span>
            </label>
            }
          </div>
          }
        </div>
        <button type="button" class="primary-action" (click)="createWhitelistEntry()" [disabled]="!!loadingAction">
          Add
        </button>
      </form>

      <div class="whitelist-toolbar" [formGroup]="whitelistFilterForm">
        <label class="whitelist-search">
          <span>Search email</span>
          <input
            type="search"
            formControlName="email"
            (input)="onWhitelistEmailSearch()"
            placeholder="Search by email"
          />
        </label>
        <label class="whitelist-filter">
          <span>Filter by role</span>
          <select
            formControlName="role"
            (change)="onWhitelistRoleFilterChange()"
          >
            <option value="">All roles</option>
            @for (role of whitelistRoles(); track role.uuid) {
            <option [value]="role.uuid">{{ role.name }}</option>
            }
          </select>
        </label>
      </div>

      <div class="result-table-wrap whitelist-table-wrap">
        <table class="result-table whitelist-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Roles</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th class="whitelist-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            @if (whitelistLoading() && whitelistEntries().length === 0) {
            <tr>
              <td colspan="5">Loading whitelist...</td>
            </tr>
            } @else if (whitelistEntries().length === 0) {
            <tr>
              <td colspan="5">No whitelist entries found.</td>
            </tr>
            } @else {
            @for (entry of whitelistEntries(); track entry.uuid) {
            <tr [class.whitelist-row--editing]="editingWhitelistUuid === entry.uuid">
              <td>{{ entry.email }}</td>
              <td>{{ roleNames(entry) }}</td>
              <td>{{ formatWhitelistDate(entry.created_at) }}</td>
              <td>{{ formatWhitelistDate(entry.updated_at) }}</td>
              <td class="whitelist-actions-col">
                <div class="whitelist-row-actions">
                  <button
                    type="button"
                    class="icon-action"
                    (click)="startEditWhitelist(entry)"
                    [disabled]="!!loadingAction"
                    aria-label="Edit whitelist entry"
                    title="Edit"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path
                        d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm14.71-9.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    class="icon-action icon-action--danger"
                    (click)="requestDeleteWhitelist(entry)"
                    [disabled]="!!loadingAction"
                    aria-label="Delete whitelist entry"
                    title="Delete"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path
                        d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
            }
            }
          </tbody>
        </table>
      </div>

      <div class="whitelist-pagination">
        <button
          type="button"
          class="ghost-action"
          (click)="goWhitelistPage(-1)"
          [disabled]="!canGoWhitelistPrev() || !!loadingAction || whitelistLoading()"
        >
          Previous
        </button>
        <span>
          Page {{ whitelistPage() }} of {{ whitelistTotalPages() }}
          ({{ whitelistTotal() }} total)
        </span>
        <button
          type="button"
          class="ghost-action"
          (click)="goWhitelistPage(1)"
          [disabled]="!canGoWhitelistNext() || !!loadingAction || whitelistLoading()"
        >
          Next
        </button>
      </div>

      @if (editingWhitelistUuid) {
      <form [formGroup]="whitelistUpdateForm" class="dashboard-form dashboard-form--stacked whitelist-edit-form">
        <h3>Edit entry</h3>
        <label>
          <span>Email</span>
          <input type="email" formControlName="email" />
        </label>
        <div class="role-multiselect" (appClickOutside)="closeRoleDropdowns()">
          <span class="field-label">Roles</span>
          <button
            type="button"
            class="role-multiselect-trigger"
            (click)="toggleEditRolesOpen()"
            [attr.aria-expanded]="editRolesOpen"
          >
            {{ selectedRolesLabel(editRoleUuids) }}
            <span class="role-caret" [class.role-caret--open]="editRolesOpen" aria-hidden="true"></span>
          </button>
          @if (editRolesOpen) {
          <div class="role-multiselect-panel">
            @for (role of whitelistRoles(); track role.uuid) {
            <label class="role-option">
              <input
                type="checkbox"
                [checked]="isEditRoleSelected(role.uuid)"
                (change)="toggleEditRole(role.uuid)"
              />
              <span>{{ role.name }}</span>
            </label>
            }
          </div>
          }
        </div>
        <div class="dashboard-actions">
          <button type="button" class="primary-action" (click)="updateWhitelistEntry()" [disabled]="!!loadingAction">
            Update
          </button>
          <button type="button" class="ghost-action" (click)="cancelEditWhitelist()" [disabled]="!!loadingAction">
            Cancel
          </button>
        </div>
      </form>
      }

      @if (pendingWhitelistDelete; as pendingDelete) {
      <div
        class="confirm-modal-backdrop"
        (click)="cancelDeleteWhitelist()"
        role="presentation"
      >
        <div
          class="confirm-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="whitelist-delete-title"
          (click)="$event.stopPropagation()"
        >
          <h3 id="whitelist-delete-title">Delete whitelist entry?</h3>
          <p>
            Are you sure you want to remove
            <strong>{{ pendingDelete.email }}</strong>
            from the whitelist? This cannot be undone.
          </p>
          <div class="dashboard-actions">
            <button
              type="button"
              class="danger-action"
              (click)="confirmDeleteWhitelist()"
              [disabled]="!!loadingAction"
            >
              {{ loadingAction === 'whitelist-delete' ? 'Deleting...' : 'Delete' }}
            </button>
            <button
              type="button"
              class="ghost-action"
              (click)="cancelDeleteWhitelist()"
              [disabled]="!!loadingAction"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
      }
    </article>
    }

    @if (activePanel() === 'client-accounts') {
    <article class="dashboard-card dashboard-card--wide">
      <h2>Client accounts</h2>

      <div class="result-table-wrap client-accounts-table-wrap">
        <table class="result-table client-accounts-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Roles</th>
              <th>Verified</th>
              <th>Plan</th>
              <th>Reqs</th>
              <th>Monthly Reqs</th>
              <th>Cycle Start</th>
              <th>Joined</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th class="client-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            @if (clientAccountsLoading() && clientAccounts().length === 0) {
            <tr>
              <td colspan="12">Loading client accounts...</td>
            </tr>
            } @else if (clientAccounts().length === 0) {
            <tr>
              <td colspan="12">No client accounts found.</td>
            </tr>
            } @else {
            @for (account of clientAccounts(); track account.uuid) {
            <tr [class.client-row--editing]="editingClientAccountUuid === account.uuid">
              <td>{{ account.name }}</td>
              <td>{{ account.email }}</td>
              <td>{{ clientRoleNames(account) }}</td>
              <td>{{ account.verified ? 'Yes' : 'No' }}</td>
              <td>{{ planName(account) }}</td>
              <td>{{ account.reqs ?? '—' }}</td>
              <td>{{ account.monthly_reqs ?? '—' }}</td>
              <td>{{ formatAdminDateTime(account.cycle_start_at) || '—' }}</td>
              <td>{{ formatAdminDate(account.joined) || '—' }}</td>
              <td>{{ formatAdminDateTime(account.created_at) }}</td>
              <td>{{ formatAdminDateTime(account.updated_at) }}</td>
              <td class="client-actions-col">
                <button
                  type="button"
                  class="icon-action"
                  (click)="startEditClientAccount(account)"
                  [disabled]="!!loadingAction"
                  aria-label="Edit client account"
                  title="Edit"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path
                      d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm14.71-9.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </td>
            </tr>
            }
            }
          </tbody>
        </table>
      </div>

      <div class="whitelist-pagination">
        <button
          type="button"
          class="ghost-action"
          (click)="goClientAccountsPage(-1)"
          [disabled]="!canGoClientAccountsPrev() || !!loadingAction || clientAccountsLoading()"
        >
          Previous
        </button>
        <span>
          Page {{ clientAccountsPage() }} of {{ clientAccountsTotalPages() }}
          ({{ clientAccountsTotal() }} total)
        </span>
        <button
          type="button"
          class="ghost-action"
          (click)="goClientAccountsPage(1)"
          [disabled]="!canGoClientAccountsNext() || !!loadingAction || clientAccountsLoading()"
        >
          Next
        </button>
      </div>

      @if (editingClientAccountUuid) {
      <form
        [formGroup]="clientPatchForm"
        class="dashboard-form dashboard-form--stacked client-edit-form"
      >
        <h3>Edit account</h3>
        <div class="client-edit-grid">
          <label>
            <span>Name</span>
            <input type="text" formControlName="name" />
          </label>
          <label>
            <span>Email</span>
            <input type="email" formControlName="email" />
          </label>
          <label class="checkbox-label client-verified-label">
            <input type="checkbox" formControlName="verified" />
            <span>Verified</span>
          </label>
          <label>
            <span>Monthly plan</span>
            <select formControlName="monthlyPlanId">
              @for (plan of plans; track plan.id) {
              <option [ngValue]="plan.id">{{ plan.name }}</option>
              }
            </select>
          </label>
          <label>
            <span>Reqs</span>
            <input type="number" formControlName="reqs" min="0" />
          </label>
          <label>
            <span>Monthly reqs</span>
            <input type="number" formControlName="monthlyReqs" min="0" />
          </label>
          <label>
            <span>Cycle start</span>
            <input type="datetime-local" formControlName="cycleStartAt" />
          </label>
          <div class="role-multiselect" (appClickOutside)="closeRoleDropdowns()">
            <span class="field-label">Roles</span>
            <button
              type="button"
              class="role-multiselect-trigger"
              (click)="toggleEditClientRolesOpen()"
              [attr.aria-expanded]="editClientRolesOpen"
            >
              {{ selectedClientRolesLabel(editClientRoleUuids) }}
              <span class="role-caret" [class.role-caret--open]="editClientRolesOpen" aria-hidden="true"></span>
            </button>
            @if (editClientRolesOpen) {
            <div class="role-multiselect-panel">
              @for (role of clientAvailableRoles(); track role.uuid) {
              <label class="role-option">
                <input
                  type="checkbox"
                  [checked]="isEditClientRoleSelected(role.uuid)"
                  (change)="toggleEditClientRole(role.uuid)"
                />
                <span>{{ role.name }}</span>
              </label>
              }
            </div>
            }
          </div>
          <label>
            <span>Operations password</span>
            <input type="password" formControlName="operationsPassword" autocomplete="current-password" />
          </label>
        </div>
        <div class="dashboard-actions">
          <button
            type="button"
            class="primary-action"
            (click)="updateClientAccount()"
            [disabled]="!!loadingAction"
          >
            {{ loadingAction === 'client-account-update' ? 'Updating...' : 'Update' }}
          </button>
          <button
            type="button"
            class="ghost-action"
            (click)="cancelEditClientAccount()"
            [disabled]="!!loadingAction"
          >
            Cancel
          </button>
        </div>
      </form>
      }
    </article>
    }

    @if (activePanel() === 'roles') {
    <article class="dashboard-card dashboard-card--wide">
      <h2>Roles</h2>

      <form [formGroup]="roleCreateForm" class="dashboard-form whitelist-add-form">
        <label>
          <span>Name</span>
          <input type="text" formControlName="name" />
        </label>
        <div class="role-multiselect" (appClickOutside)="closeRoleDropdowns()">
          <span class="field-label">Permissions</span>
          <button
            type="button"
            class="role-multiselect-trigger"
            (click)="toggleCreatePermissionsOpen()"
            [attr.aria-expanded]="createPermissionsOpen"
          >
            {{ selectedPermissionsLabel(createPermissionUuids) }}
            <span class="role-caret" [class.role-caret--open]="createPermissionsOpen" aria-hidden="true"></span>
          </button>
          @if (createPermissionsOpen) {
          <div class="role-multiselect-panel">
            @for (permission of availablePermissions(); track permission.uuid) {
            <label class="role-option">
              <input
                type="checkbox"
                [checked]="isCreatePermissionSelected(permission.uuid)"
                (change)="toggleCreatePermission(permission.uuid)"
              />
              <span>{{ permission.name }}</span>
            </label>
            }
          </div>
          }
        </div>
        <button type="button" class="primary-action" (click)="createCatalogRole()" [disabled]="!!loadingAction">
          Add
        </button>
      </form>

      <div class="whitelist-toolbar" [formGroup]="roleSearchForm">
        <label class="whitelist-search">
          <span>Search</span>
          <input
            type="search"
            formControlName="search"
            (input)="onRolesSearch()"
            placeholder="Search roles"
          />
        </label>
      </div>

      <div class="result-table-wrap whitelist-table-wrap">
        <table class="result-table whitelist-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Permissions</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th class="whitelist-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            @if (catalogRolesLoading() && catalogRoles().length === 0) {
            <tr>
              <td colspan="5">Loading roles...</td>
            </tr>
            } @else if (catalogRoles().length === 0) {
            <tr>
              <td colspan="5">No roles found.</td>
            </tr>
            } @else {
            @for (role of catalogRoles(); track role.uuid) {
            <tr [class.whitelist-row--editing]="editingCatalogRoleUuid === role.uuid">
              <td>{{ role.name }}</td>
              <td>{{ rolePermissionNames(role) }}</td>
              <td>{{ formatAdminDateTime(role.created_at) }}</td>
              <td>{{ formatAdminDateTime(role.updated_at) }}</td>
              <td class="whitelist-actions-col">
                <button
                  type="button"
                  class="icon-action"
                  (click)="startEditCatalogRole(role)"
                  [disabled]="!!loadingAction"
                  aria-label="Edit role"
                  title="Edit"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path
                      d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm14.71-9.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </td>
            </tr>
            }
            }
          </tbody>
        </table>
      </div>

      <div class="whitelist-pagination">
        <button
          type="button"
          class="ghost-action"
          (click)="goCatalogRolesPage(-1)"
          [disabled]="!canGoCatalogRolesPrev() || !!loadingAction || catalogRolesLoading()"
        >
          Previous
        </button>
        <span>
          Page {{ catalogRolesPage() }} of {{ catalogRolesTotalPages() }}
          ({{ catalogRolesTotal() }} total)
        </span>
        <button
          type="button"
          class="ghost-action"
          (click)="goCatalogRolesPage(1)"
          [disabled]="!canGoCatalogRolesNext() || !!loadingAction || catalogRolesLoading()"
        >
          Next
        </button>
      </div>

      @if (editingCatalogRoleUuid) {
      <form [formGroup]="roleUpdateForm" class="dashboard-form dashboard-form--stacked whitelist-edit-form">
        <h3>Edit role</h3>
        <label>
          <span>Name</span>
          <input type="text" formControlName="name" />
        </label>
        <div class="role-multiselect" (appClickOutside)="closeRoleDropdowns()">
          <span class="field-label">Permissions</span>
          <button
            type="button"
            class="role-multiselect-trigger"
            (click)="toggleEditPermissionsOpen()"
            [attr.aria-expanded]="editPermissionsOpen"
          >
            {{ selectedPermissionsLabel(editPermissionUuids) }}
            <span class="role-caret" [class.role-caret--open]="editPermissionsOpen" aria-hidden="true"></span>
          </button>
          @if (editPermissionsOpen) {
          <div class="role-multiselect-panel">
            @for (permission of availablePermissions(); track permission.uuid) {
            <label class="role-option">
              <input
                type="checkbox"
                [checked]="isEditPermissionSelected(permission.uuid)"
                (change)="toggleEditPermission(permission.uuid)"
              />
              <span>{{ permission.name }}</span>
            </label>
            }
          </div>
          }
        </div>
        <div class="dashboard-actions">
          <button type="button" class="primary-action" (click)="updateCatalogRole()" [disabled]="!!loadingAction">
            {{ loadingAction === 'role-update' ? 'Updating...' : 'Update' }}
          </button>
          <button type="button" class="ghost-action" (click)="cancelEditCatalogRole()" [disabled]="!!loadingAction">
            Cancel
          </button>
        </div>
      </form>
      }
    </article>
    }

    @if (activePanel() === 'permissions') {
    <article class="dashboard-card dashboard-card--wide">
      <h2>Permissions</h2>

      <form [formGroup]="permissionCreateForm" class="dashboard-form whitelist-add-form permissions-add-form">
        <label>
          <span>Name</span>
          <input type="text" formControlName="name" />
        </label>
        <button type="button" class="primary-action" (click)="createCatalogPermission()" [disabled]="!!loadingAction">
          Add
        </button>
      </form>

      <div class="whitelist-toolbar" [formGroup]="permissionSearchForm">
        <label class="whitelist-search">
          <span>Search</span>
          <input
            type="search"
            formControlName="search"
            (input)="onPermissionsSearch()"
            placeholder="Search permissions"
          />
        </label>
      </div>

      <div class="result-table-wrap whitelist-table-wrap">
        <table class="result-table whitelist-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th class="whitelist-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            @if (catalogPermissionsLoading() && catalogPermissions().length === 0) {
            <tr>
              <td colspan="4">Loading permissions...</td>
            </tr>
            } @else if (catalogPermissions().length === 0) {
            <tr>
              <td colspan="4">No permissions found.</td>
            </tr>
            } @else {
            @for (permission of catalogPermissions(); track permission.uuid) {
            <tr [class.whitelist-row--editing]="editingCatalogPermissionUuid === permission.uuid">
              <td>{{ permission.name }}</td>
              <td>{{ formatAdminDateTime(permission.created_at) }}</td>
              <td>{{ formatAdminDateTime(permission.updated_at) }}</td>
              <td class="whitelist-actions-col">
                <button
                  type="button"
                  class="icon-action"
                  (click)="startEditCatalogPermission(permission)"
                  [disabled]="!!loadingAction"
                  aria-label="Edit permission"
                  title="Edit"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path
                      d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm14.71-9.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </td>
            </tr>
            }
            }
          </tbody>
        </table>
      </div>

      <div class="whitelist-pagination">
        <button
          type="button"
          class="ghost-action"
          (click)="goCatalogPermissionsPage(-1)"
          [disabled]="!canGoCatalogPermissionsPrev() || !!loadingAction || catalogPermissionsLoading()"
        >
          Previous
        </button>
        <span>
          Page {{ catalogPermissionsPage() }} of {{ catalogPermissionsTotalPages() }}
          ({{ catalogPermissionsTotal() }} total)
        </span>
        <button
          type="button"
          class="ghost-action"
          (click)="goCatalogPermissionsPage(1)"
          [disabled]="!canGoCatalogPermissionsNext() || !!loadingAction || catalogPermissionsLoading()"
        >
          Next
        </button>
      </div>

      @if (editingCatalogPermissionUuid) {
      <form [formGroup]="permissionUpdateForm" class="dashboard-form dashboard-form--stacked whitelist-edit-form">
        <h3>Edit permission</h3>
        <label>
          <span>Name</span>
          <input type="text" formControlName="name" />
        </label>
        <div class="dashboard-actions">
          <button type="button" class="primary-action" (click)="updateCatalogPermission()" [disabled]="!!loadingAction">
            {{ loadingAction === 'permission-update' ? 'Updating...' : 'Update' }}
          </button>
          <button type="button" class="ghost-action" (click)="cancelEditCatalogPermission()" [disabled]="!!loadingAction">
            Cancel
          </button>
        </div>
      </form>
      }
    </article>
    }

    @if (activePanel() === 'api-keys') {
    <article class="dashboard-card dashboard-card--wide">
      <h2>API keys</h2>
      <form [formGroup]="apiKeysPageForm" class="dashboard-form api-key-form">
        <label>
          <span>Page</span>
          <input type="number" formControlName="page" min="1" />
        </label>
        <button type="button" class="primary-action" (click)="loadApiKeys()" [disabled]="!!loadingAction">
          List API Keys
        </button>
      </form>
      <form [formGroup]="apiKeyLookupForm" class="dashboard-form api-key-form">
        <label>
          <span>API key ID</span>
          <input type="text" formControlName="keyId" />
        </label>
        <label>
          <span>Operations password for revoke</span>
          <input type="password" formControlName="operationsPassword" />
        </label>
        <button type="button" class="primary-action" (click)="getApiKey()" [disabled]="!!loadingAction">
          Get
        </button>
        <button type="button" class="ghost-action" (click)="revokeApiKey()" [disabled]="!!loadingAction">
          Revoke
        </button>
      </form>
    </article>
    }

    }

    @for (result of dashboardResults | keyvalue; track result.key) {
    <section class="dashboard-result result-card">
      <h2>{{ result.key }}</h2>
      @if (hasResultTables(result.value)) {
      @for (table of resultTables(result.value); track table.title) {
      <div class="result-table-block">
        <h3>{{ table.title }}</h3>
        <div class="result-table-wrap">
          <table class="result-table">
            <thead>
              <tr>
                @for (column of table.columns; track column) {
                <th>{{ formatColumn(column) }}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of table.rows; track $index) {
              <tr>
                @for (column of table.columns; track column) {
                <td>{{ formatCell(row[column]) }}</td>
                }
              </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
      }
      } @else {
      <pre>{{ result.value | json }}</pre>
      }
    </section>
    }
  </section>
  </div>
  }
</main>
